'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m, useReducedMotion } from 'framer-motion'
import { fadeUp, fadeUpItem, staggerContainer, VIEWPORT } from '@/lib/motion'

// Reusable Framer reveal. Wraps content in a scroll-triggered fade-up,
// or a stagger container whose <RevealItem> children settle in sequence.
// LazyMotion + domAnimation keeps the bundle light. Reduced motion resolves
// to the end state instantly (initial={false}).
//
//   <Reveal stagger>
//     <RevealItem>…</RevealItem>
//     <RevealItem>…</RevealItem>
//   </Reveal>

export function Reveal({
  children,
  className,
  stagger = false,
  as = 'div',
}: {
  children: React.ReactNode
  className?: string
  stagger?: boolean
  as?: 'div' | 'section' | 'ul' | 'ol'
}) {
  const reduce = useReducedMotion()
  const Comp = m[as]
  return (
    <LazyMotion features={domAnimation}>
      <Comp
        className={className}
        initial={reduce ? false : 'hidden'}
        whileInView={reduce ? undefined : 'show'}
        viewport={VIEWPORT}
        variants={stagger ? staggerContainer : fadeUp}
      >
        {children}
      </Comp>
    </LazyMotion>
  )
}

export function RevealItem({
  children,
  className,
  as = 'div',
}: {
  children: React.ReactNode
  className?: string
  as?: 'div' | 'li'
}) {
  const reduce = useReducedMotion()
  const Comp = m[as]
  return (
    <Comp className={className} initial={reduce ? false : undefined} variants={reduce ? undefined : fadeUpItem}>
      {children}
    </Comp>
  )
}
